"use client";

import { useEffect, useRef } from "react";
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Group,
  Mesh,
  SphereGeometry,
  ShaderMaterial,
  SRGBColorSpace,
} from "three";

/**
 * The Percentile Labs mark as a genuine 3D globe. Rather than mapping the flat
 * art (which reads as a target and carries the centre pearl), we generate the
 * stripes in a shader: true angular latitude bands that foreshorten toward the
 * limb like a real sphere, a gentle spiral so the slow pole-spin is legible, and
 * camera-facing shading for volume. No central pole/pin. Raw three.js, mirroring
 * RubiksCube.tsx.
 */

// Tunables — dial against the logo.
const TILT_X = -0.42; // look down onto the globe (radians)
const SPEED = 0.14; // rad/s about the pole — slow
const BAND_FREQ = 3.0; // ~how many light bands pole -> equator
// Whole twists per revolution keeps the spiral seamless at the theta wrap.
const TWISTS = 1; // 0 = flat concentric rings; 1 = one gentle twist
const SPIRAL = TWISTS / (2 * Math.PI);

const VERT = /* glsl */ `
  varying vec3 vObjPos;
  varying vec3 vWorldNormal;
  void main() {
    vObjPos = normalize(position);
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */ `
  precision highp float;
  varying vec3 vObjPos;
  varying vec3 vWorldNormal;
  uniform float uBandFreq;
  uniform float uSpiral;
  uniform vec3 uBandColor;
  uniform vec3 uBgColor;
  const float PI = 3.14159265359;
  void main() {
    float phi = acos(clamp(vObjPos.z, -1.0, 1.0));
    float theta = atan(vObjPos.y, vObjPos.x);
    float v = phi / (PI * 0.5) * uBandFreq + theta * uSpiral;
    float f = fract(v + 0.5);
    float w = min(fwidth(v) * 1.1 + 0.001, 0.03);
    float band = 1.0 - (smoothstep(0.5 - w, 0.5 + w, f) - smoothstep(1.0 - w, 1.0 + w, f));
    float shade = mix(0.66, 1.0, clamp(vWorldNormal.z, 0.0, 1.0));
    vec3 col = mix(uBgColor * shade, uBandColor * shade, band);
    gl_FragColor = vec4(col, 1.0);
  }
`;

function parseHex(hex: string): [number, number, number] {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return [0, 0, 0];
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
}

function getGlobeColors(): { primary: [number, number, number]; bg: [number, number, number] } {
  if (typeof window === "undefined") return { primary: [0, 0, 0], bg: [1, 1, 1] };
  const style = getComputedStyle(document.documentElement);
  const isDark = document.documentElement.classList.contains("dark");
  return {
    primary: parseHex(style.getPropertyValue("--primary").trim()),
    bg: isDark ? [0, 0, 0] : [1, 1, 1],
  };
}

export default function PercentileDonut({
  className,
  speed = SPEED,
}: {
  className?: string;
  speed?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new Scene();
    const camera = new PerspectiveCamera(30, 1, 0.1, 50);
    camera.position.set(0, 0, 4.2);

    let renderer: WebGLRenderer;
    try {
      renderer = new WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return; // WebGL unavailable — leave the empty stand-in.
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const root = new Group();
    root.rotation.set(TILT_X, 0, 0);
    scene.add(root);

    const geo = new SphereGeometry(1, 160, 160);
    const colors = getGlobeColors();
    const mat = new ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uBandFreq: { value: BAND_FREQ },
        uSpiral: { value: SPIRAL },
        uBandColor: { value: colors.primary },
        uBgColor: { value: colors.bg },
      },
    });
    const mesh = new Mesh(geo, mat);
    root.add(mesh);

    const resize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    let visible = true;
    const io = new IntersectionObserver(([e]) => (visible = e.isIntersecting));
    io.observe(container);

    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(now - last, 100) / 1000;
      last = now;
      if (!visible || document.hidden) return;
      const c = getGlobeColors();
      mat.uniforms.uBandColor.value = c.primary;
      mat.uniforms.uBgColor.value = c.bg;
      mesh.rotation.z += dt * speed;
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [speed]);

  return <div ref={containerRef} className={className} aria-label="Percentile Labs" />;
}
